import React, { useState } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import '@/index.css';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Navbar, { SUPERADMIN_EMAIL, TopPlanBar } from './components/Navbar';
import Home from './pages/Home';
import Generator from './pages/Generator';
import History from './pages/History';
import Customers from './pages/Customers';
import Items from './pages/Items';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import Success from './pages/Success';
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import Admin from './pages/Admin';
import Help from './pages/Help';
import Legal from './pages/Legal';
import Privacy from './pages/Privacy';
import Changelog from './pages/Changelog';
import BlogPost from './pages/BlogPost';
import Blog from './pages/Blog';
import { Toaster } from 'sonner';
import { useAuth } from './contexts/AuthContext';

// Fixed-height overflow-hidden layout only on the Generator route
const FIXED_ROUTES = new Set(['/create']);

function RequireLogin({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return null;
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function RequireSuperadmin({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return null;
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  if ((user.email || '').toLowerCase() !== SUPERADMIN_EMAIL) return <Navigate to="/dashboard" replace />;
  return children;
}

function Shell() {
  const { pathname } = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fixed = FIXED_ROUTES.has(pathname);
  return (
    <div className={`${fixed ? 'lg:h-screen lg:overflow-hidden' : 'min-h-screen'} bg-slate-50/50`}>
      <Navbar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        onToggle={() => setSidebarCollapsed((v) => !v)}
      />
      <TopPlanBar
        collapsed={sidebarCollapsed}
        onMobileMenu={() => setMobileMenuOpen(true)}
        onToggle={() => setSidebarCollapsed((v) => !v)}
      />
      <main className={`pt-14 sm:pt-16 transition-all duration-200 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}${fixed ? ' lg:h-screen lg:overflow-hidden' : ''}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/create" element={<Generator />} />
          <Route path="/history" element={<History />} />
          <Route path="/sales" element={<RequireLogin><Sales /></RequireLogin>} />
          <Route path="/reports" element={<Navigate to="/sales" replace />} />
          <Route path="/customers" element={<RequireLogin><Customers /></RequireLogin>} />
          <Route path="/items" element={<RequireLogin><Items /></RequireLogin>} />
          <Route path="/settings" element={<RequireLogin><Settings /></RequireLogin>} />
          <Route path="/business-profile" element={<RequireLogin><Settings /></RequireLogin>} />
          <Route path="/dashboard" element={<RequireLogin><Dashboard /></RequireLogin>} />
          <Route path="/help" element={<RequireLogin><Help /></RequireLogin>} />
          <Route path="/admin" element={<RequireSuperadmin><Admin /></RequireSuperadmin>} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/plan" element={<Pricing />} />
          <Route path="/pricing" element={<Navigate to="/plan" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/success" element={<Success />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
